/**
 * De gebeurtenissendobbelsteen.
 *
 * Volgorde binnen een beurt: gooien → **gebeurtenis afhandelen** → opbrengst uitdelen →
 * hoofdfase (handboek p. 12: "Eerst wordt altijd het resultaat van de
 * gebeurtenissendobbelsteen vastgesteld"). Die volgorde is niet vrijblijvend: de
 * roofoverval kijkt naar je voorraad, en die is vóór de opbrengst kleiner.
 *
 * Drie van de vijf gebeurtenissen vragen een keuze van een speler. Die keuze is een
 * zet, geen automatisme — de kern zet daarom een openstaande keuze in de stand en
 * wacht. Zie kennisbank 02 §2.1.
 */
import { LANDSCAPE_RESOURCE, LIMITS, type Resource, type Seat } from '@catan/contracts';
import { pick, type Rng } from './rng.js';
import type { GameState, LandscapeTile, PlayerState, Side } from './state.js';
import { opponentOf } from './state.js';

/** Waar de kern op wacht voordat de beurt verder kan. */
export type PendingChoice =
  /** Kies een landschap dat er een grondstof bij krijgt. */
  | { readonly kind: 'kies-opbrengst'; readonly seat: Seat; readonly reason: 'goed-jaar' | 'riddertoernooi' }
  /** Kies bij de tegenstander een grondstof om weg te nemen. */
  | { readonly kind: 'kies-buit'; readonly seat: Seat };

/** Handelspunten van een speler: de som over zijn gebouwde uitbreidingen. */
export function commercePoints(
  player: PlayerState,
  pointsOf: (cardId: number) => number,
): number {
  return player.principality.buildings
    .flatMap((b) => [...b.expansions.boven, ...b.expansions.onder])
    .reduce((sum, cardId) => sum + pointsOf(cardId), 0);
}

/**
 * Onbeschermde grondstoffen: alles wat niet naast een pakhuis ligt.
 *
 * Een pakhuis beschermt de landschappen aan zijn eigen kant van het bouwwerk waar hij
 * bij staat (kennisbank 02 §2.1, kaarttekst "Pakhuis").
 */
export function unprotectedResources(
  player: PlayerState,
  isWarehouse: (cardId: number) => boolean,
): number {
  const beschermd = new Set<LandscapeTile>();
  for (const building of player.principality.buildings) {
    for (const side of ['boven', 'onder'] as const) {
      if (!building.expansions[side].some(isWarehouse)) continue;
      for (const tile of player.principality.landscapes) {
        const grenst = tile.column === building.column - 1 || tile.column === building.column + 1;
        if (grenst && tile.side === side) beschermd.add(tile);
      }
    }
  }
  return player.principality.landscapes
    .filter((tile) => !beschermd.has(tile))
    .reduce((sum, tile) => sum + tile.resources, 0);
}

/**
 * Roofoverval: wie meer dan zeven onbeschermde grondstoffen heeft, verliest al zijn
 * erts en wol. Geldt niet in de eerste twee beurten (fysieke regels 1996).
 */
export function applyBrigands(
  player: PlayerState,
  isWarehouse: (cardId: number) => boolean,
): { player: PlayerState; lost: number } {
  if (unprotectedResources(player, isWarehouse) <= LIMITS.brigandThreshold) {
    return { player, lost: 0 };
  }
  let lost = 0;
  const landscapes = player.principality.landscapes.map((tile) => {
    const resource = LANDSCAPE_RESOURCE[tile.landscape];
    if (resource !== 'erts' && resource !== 'wol') return tile;
    lost += tile.resources;
    return { ...tile, resources: 0 };
  });
  return {
    player: { ...player, principality: { ...player.principality, landscapes } },
    lost,
  };
}

/** Legt één grondstof op een landschap; geeft aan hoeveel er daadwerkelijk bij kwam. */
export function addResource(
  player: PlayerState,
  column: number,
  side: 'boven' | 'onder',
): { player: PlayerState; gained: number } {
  let gained = 0;
  const landscapes = player.principality.landscapes.map((tile) => {
    if (tile.column !== column || tile.side !== side) return tile;
    if (tile.resources >= LIMITS.resourcesPerLandscape) return tile;
    gained = 1;
    return { ...tile, resources: tile.resources + 1 };
  });
  return {
    player: { ...player, principality: { ...player.principality, landscapes } },
    gained,
  };
}

/** Haalt één grondstof weg van een landschap. */
export function removeResource(
  player: PlayerState,
  column: number,
  side: 'boven' | 'onder',
): { player: PlayerState; taken: Resource | undefined } {
  let taken: Resource | undefined;
  const landscapes = player.principality.landscapes.map((tile) => {
    if (tile.column !== column || tile.side !== side || tile.resources === 0) return tile;
    taken = LANDSCAPE_RESOURCE[tile.landscape];
    return { ...tile, resources: tile.resources - 1 };
  });
  return {
    player: { ...player, principality: { ...player.principality, landscapes } },
    taken,
  };
}

/**
 * Wie heeft er het meeste van iets? Bij gelijkspel niemand — zo werkt het ook bij de
 * speelfiguren (fysieke regels: "If there is a tie, neither player controls the token").
 */
export function leaderBy(
  state: GameState,
  score: (player: PlayerState) => number,
): Seat | undefined {
  const a = score(state.players[0]);
  const b = score(state.players[1]);
  if (a === b) return undefined;
  return a > b ? 0 : 1;
}

export { opponentOf };

/** Legt een willekeurige grondstof bij een speler neer. Vol landschap = niets. */
export function giveRandom(
  player: PlayerState,
  rng: Rng,
): { player: PlayerState; rng: Rng; gained: boolean } {
  const ruimte = player.principality.landscapes.filter((t) => t.resources < 3);
  if (ruimte.length === 0) return { player, rng, gained: false };
  const keuze = pick(rng, ruimte);
  const result = addResource(player, keuze.value.column, keuze.value.side);
  return { player: result.player, rng: keuze.rng, gained: result.gained > 0 };
}

/**
 * Waar een gestolen grondstof bij deze speler terecht kan — of nergens.
 *
 * `undefined` is een echt antwoord en geen randgeval: het handboek maakt er een regel van.
 * "Grondstoffen kunnen alleen geroofd worden, als je ze op je landen van dat type kunt
 * plaatsen" (p. 11 §5), en bij de Handelaar: "Als je bijvoorbeeld op je boskaart al 3 hout
 * in voorraad hebt, kun je geen hout meer krijgen" (p. 12 §9).
 *
 * Hier stond een terugval op `landscapes[0]`. Dat deed twee dingen fout tegelijk: had die
 * tegel geen ruimte, dan verdween de buit spoorloos — de aanroeper controleerde het niet —
 * en was hij van een ánder type, dan telde de grondstof daarna als dát type. De Rooftocht en
 * de Handelaar gebruiken allebei deze functie, dus het ging op twee plekken mis.
 */
export function naarEigenLandschap(
  player: PlayerState,
  resource: Resource,
): [number, Side] | undefined {
  const doel = player.principality.landscapes.find(
    (t) => LANDSCAPE_RESOURCE[t.landscape] === resource && t.resources < 3,
  );
  return doel === undefined ? undefined : [doel.column, doel.side];
}
